import { ClientInfoStatus } from '../../interfaces/client'
import { DEAL_STAGE_LADDER, DealStage } from '../../interfaces/deal'
import {
  PROSPECT_REPORTING_TYPES,
  PROSPECT_REPORTING_MAP,
  OPSReportingFlowStatus,
  OPSReportingStatus,
  ProspectGroupBy,
} from '../../interfaces/prospects'
import { FilterFieldType, IFilter, UNMAPPED_FILTER_VALUE } from './types'

const sqlLiteral = (value: string): string => `'${value.replace(/'/g, "''")}'`

/**
 * Aliases are quoted because a raw dbField is spliced into WHERE/ORDER BY without
 * TypeORM's alias rewriting, which would fold a camelCase alias to lowercase.
 */
export const dealIsOpenSql = (dealAlias: string): string =>
  `("${dealAlias}".outcome IS NULL AND "${dealAlias}".deleted_at IS NULL)`

/** A prospect's stage: its deal's stage while the deal is open, else NULL. */
export const dealOpenStageSql = (dealAlias: string): string =>
  `CASE WHEN ${dealIsOpenSql(dealAlias)} THEN "${dealAlias}".stage END`

/** Ladder position of a deal stage expression, so pipeline groups sort in order. */
export const dealStageRankSql = (stageSql: string): string =>
  `CASE ${stageSql} ${DEAL_STAGE_LADDER.map(
    (stage, index) => `WHEN ${sqlLiteral(stage)} THEN ${index}`,
  ).join(' ')} END`

/** The name a row's BDO sorts by, resolved the same way as prospectOwnerSql. */
export const prospectOwnerNameSql = (
  dealAlias: string,
  dealOwnerAlias: string,
  companyOwnerAlias: string,
): string =>
  `COALESCE(CASE WHEN ${dealIsOpenSql(dealAlias)} THEN "${dealOwnerAlias}".first_name END, "${companyOwnerAlias}".first_name)`

/**
 * The BDO a prospect row belongs to: the owner of its OPEN deal, else the company owner,
 * so a company whose deal closed long ago isn't attributed to that deal's owner.
 */
export const prospectOwnerSql = (dealAlias: string, companyAlias: string): string =>
  `COALESCE(CASE WHEN ${dealIsOpenSql(dealAlias)} THEN "${dealAlias}".owner_id END, "${companyAlias}".owner_id)`

export const dealNotClosedFirstSql = (alias: string): string => `("${alias}".outcome IS NOT NULL)`

export const dealLastActivitySql = (alias: string): string => `"${alias}".last_activity_date`

/**
 * "The company's current deal": an open deal first, then the latest close, then most recent
 * activity, then the newest. Callers exclude deleted deals themselves. DESC needs NULLS LAST
 * because Postgres defaults DESC to NULLS FIRST.
 */
export const currentCompanyDealOrder = (
  alias: string,
): Array<[sql: string, direction: 'ASC' | 'DESC', nulls?: 'NULLS LAST']> => [
  [dealNotClosedFirstSql(alias), 'ASC'],
  [`"${alias}".closed_at`, 'DESC', 'NULLS LAST'],
  [dealLastActivitySql(alias), 'DESC', 'NULLS LAST'],
  [`"${alias}".created_at`, 'DESC'],
]

export const currentCompanyDealOrderSql = (alias: string): string =>
  currentCompanyDealOrder(alias)
    .map((term) => term.join(' '))
    .join(', ')

export const PROSPECT_REPORTING_LIST_FILTERS_CONFIG = [
  {
    field: 'expand',
    type: FilterFieldType.Empty,
    title: '',
    align: 'center',
    isSortable: false,
    excludeFilter: true,
  },
  {
    field: 'prospect',
    dbField: 'client_name',
    type: FilterFieldType.Autocomplete,
    excludeFilter: true,
    alias: 'opsReportingFlows',
    isSortable: true,
    placeholder: 'Prospect',
    title: 'Prospect',
  },
  {
    field: 'type',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    alias: 'opsReportingFlows',
    title: 'Report type',
    options: Object.values(PROSPECT_REPORTING_TYPES).map((type) => ({
      value: type,
      label: PROSPECT_REPORTING_MAP[type],
    })),
  },
  {
    field: 'preprocessingSummary',
    type: FilterFieldType.Empty,
    title: 'Summary',
    align: 'left',
    isSortable: false,
    excludeFilter: true,
  },
  {
    field: 'boxLink',
    type: FilterFieldType.Empty,
    title: 'Folder',
    align: 'left',
    isSortable: true,
    isCustom: true,
  },
  {
    field: 'createdAt',
    dbField: `"filesSubQuery"."createdAt"`,
    type: FilterFieldType.Date,
    align: 'right',
    isSortable: true,
    title: 'Submitted',
    excludeFullSearchFilter: true,
    aggColumn: true,
  },
  {
    field: 'completedAt',
    dbField: 'completed_at',
    type: FilterFieldType.Date,
    align: 'right',
    isSortable: true,
    title: 'Completed',
    excludeFullSearchFilter: true,
  },
  {
    field: 'monthsProcessed',
    type: FilterFieldType.Empty,
    align: 'right',
    isSortable: false,
    excludeFullSearchFilter: true,
    title: 'Processed',
    excludeFilter: true,
  },
  {
    field: 'statuses',
    dbField: 'status',
    alias: 'opsReportingFlows',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    title: 'Status',
    options: [
      OPSReportingFlowStatus.NotStarted,
      OPSReportingFlowStatus.SelectFile,
      OPSReportingFlowStatus.ReadyToMap,
      OPSReportingFlowStatus.Complete,
      OPSReportingFlowStatus.Waived,
    ].map((status) => ({
      value: status,
      label: status,
    })),
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'salesforceOwner',
        type: FilterFieldType.List,
      },
      {
        field: 'statuses',
        type: FilterFieldType.List,
      },
    ],
    quickFilters: [
      {
        title: 'All',
        filters: {},
        isHidden: false,
      },
      {
        title: 'Action Required',
        filters: {
          statuses: [OPSReportingFlowStatus.SelectFile, OPSReportingFlowStatus.ReadyToMap],
        },
        isHidden: false,
      },
    ],
  },
] as const satisfies IFilter[]

/**
 * The CRM's last touch on a prospect: the deal's activity or the company's last
 * interaction, never the analysis side's report activity.
 */
export const prospectLastActivitySql = (dealAlias: string, companyAlias: string): string =>
  `GREATEST("${dealAlias}".last_activity_date, "${companyAlias}".last_interaction_date)`

/** Section and filter label for prospects with no live stage. */
export const NO_STAGE_LABEL = 'No stage'

/** The prospect views, each a tab with its own scope and grouping. */
export const PROSPECT_VIEW = {
  Pipeline: 'Pipeline',
  Qualifying: 'Qualifying',
} as const

export type ProspectView = (typeof PROSPECT_VIEW)[keyof typeof PROSPECT_VIEW]

/** The owner quick filter on prospect tabs and the Radar; its value is the viewer, set at render. */
export const MY_RECORDS_QUICK_FILTER = 'My Prospects'

// Archived versions (lost opportunities auto-archive) leave the working views.
const ACTIVE_OPS_REPORTING_STATUSES = [
  OPSReportingStatus.NotStarted,
  OPSReportingStatus.InProgress,
  OPSReportingStatus.Complete,
]

// Every open deal from OPS on, including diligence. Exported because the list's default
// and the pipeline metrics share this scope.
export const PROSPECT_PIPELINE_FILTERS = {
  dealStage: DEAL_STAGE_LADDER.filter((stage) => stage !== DealStage.Qualifying),
  clientStatus: [ClientInfoStatus.Prospect, ClientInfoStatus.DueDiligence],
  status: ACTIVE_OPS_REPORTING_STATUSES,
}

/** Which section a row belongs in: its stage on the Pipeline, its BDO on Qualifying. */
export const prospectGroupKeyOf = (
  row: { dealStage: string | null; ownerId: string | null },
  groupBy: ProspectGroupBy,
): string | null => (groupBy === 'stage' ? row.dealStage : row.ownerId)

export const PROSPECTS_LIST_FILTERS_CONFIG = [
  {
    field: 'clients',
    dbField: 'client_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Prospect',
    title: 'Prospect',
    alias: 'clientInfo',
  },
  {
    // Industry, Description and Primary Contact are early-ladder columns, hidden on the
    // Pipeline view.
    field: 'industry',
    type: FilterFieldType.Empty,
    title: 'Industry',
    align: 'left',
    isCustom: true,
    isSortable: false,
  },
  {
    field: 'shortDescription',
    type: FilterFieldType.Empty,
    title: 'Description',
    align: 'left',
    isCustom: true,
    isSortable: false,
  },
  {
    // Display-only column: edited inline, never filtered or sorted.
    field: 'nextSteps',
    type: FilterFieldType.Empty,
    title: 'Next Steps',
    align: 'left',
    isCustom: true,
    isSortable: false,
  },
  {
    field: 'primaryContact',
    type: FilterFieldType.Empty,
    title: 'Primary Contact',
    align: 'left',
    isCustom: true,
    isSortable: false,
  },
  {
    field: 'dealStage',
    dbField: dealOpenStageSql('deal'),
    type: FilterFieldType.List,
    isSortable: true,
    isMultiple: true,
    placeholder: 'Stage',
    title: 'Stage',
    options: [
      { value: UNMAPPED_FILTER_VALUE, label: NO_STAGE_LABEL },
      ...DEAL_STAGE_LADDER.map((stage) => ({ value: stage, label: stage })),
    ],
  },
  {
    field: 'lastActivityDate',
    // The CRM's last touch: the deal's activity or the company's interactions, not the
    // analysis side's report activity.
    dbField: prospectLastActivitySql('deal', 'company'),
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last Activity',
    isExternalField: true,
  },
  {
    // No `alias`: the dbField is a full expression, which getFieldName uses verbatim.
    field: 'owner',
    dbField: prospectOwnerSql('deal', 'company'),
    type: FilterFieldType.List,
    isSortable: true,
    isMultiple: true,
    placeholder: 'BDO',
    title: 'BDO',
  },
  {
    field: 'commitmentAmount',
    dbField: 'commitment_amount',
    alias: 'deal',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Commitment Amount',
  },
  {
    field: 'closeDate',
    dbField: 'close_date',
    alias: 'deal',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Close Date',
  },
  {
    field: 'riskRating',
    dbField: 'risk_rating',
    alias: 'riskRatingRawScores',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Risk',
  },
  {
    // Deal Summary status on the Pipeline; on Qualifying, documents processed ("3/5") as Doc Status.
    field: 'dealStatus',
    type: FilterFieldType.Empty,
    title: 'Status',
    align: 'left',
    isCustom: true,
    isSortable: false,
  },
  {
    field: 'referralSource',
    type: FilterFieldType.Empty,
    title: 'Referral Source',
    align: 'left',
    isCustom: true,
    isSortable: false,
  },
  {
    // Filter-only: the analysis version's status, which scopes both views to live versions.
    field: 'status',
    dbField: 'status',
    alias: 'opsReporting',
    title: 'Analysis Status',
    excludeColumn: true,
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    options: [
      {
        value: '',
        label: 'All',
      },
    ].concat(
      Object.values(OPSReportingStatus).map((status) => ({
        value: status,
        label: status,
      })),
    ),
  },
  {
    // Filter-only (no column): the prospect's client status. The list itself
    // includes both Prospect and Archived clients; quick filters use this to
    // scope by archived state.
    field: 'clientStatus',
    dbField: 'client_status',
    alias: 'clientInfo',
    type: FilterFieldType.List,
    isMultiple: true,
    excludeColumn: true,
    title: 'Client Status',
    options: Object.values(ClientInfoStatus).map((status) => ({
      value: status,
      label: status,
    })),
  },
  {
    field: 'dealCreatedAt',
    dbField: 'created_at',
    alias: 'opsReporting',
    type: FilterFieldType.Date,
    title: 'Created',
    excludeColumn: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [{ title: MY_RECORDS_QUICK_FILTER, filters: { owner: [] } }],
  },
] as const satisfies IFilter[]

/** What each prospect tab shows before any filter is touched. */
export const PROSPECT_VIEW_FILTERS = {
  [PROSPECT_VIEW.Pipeline]: PROSPECT_PIPELINE_FILTERS,
  [PROSPECT_VIEW.Qualifying]: {
    dealStage: [DealStage.Qualifying],
    clientStatus: [ClientInfoStatus.Prospect],
    status: ACTIVE_OPS_REPORTING_STATUSES,
  },
} satisfies Record<ProspectView, Record<string, string[]>>

export const PROSPECTS_INVENTORY_LIST_FILTERS_CONFIG = [
  {
    field: 'description',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'sku',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'SKU',
    title: 'SKU',
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Type',
    title: 'Type',
  },
  {
    field: 'location',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Location',
    title: 'Location',
  },
  {
    field: 'totalValue',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Value',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
  },
  {
    field: 'totalAvailability',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Availability',
  },
] as const satisfies IFilter[]

export const PROSPECTS_RECEIVABLES_LIST_FILTERS_CONFIG = [
  {
    field: 'debtor',
    dbField: 'memo',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'mapping',
    type: FilterFieldType.Empty,
    title: 'Customer',
    align: 'left',
  },
  {
    field: 'prediction',
    type: FilterFieldType.Empty,
    title: 'Prediction',
    align: 'left',
  },
  {
    field: 'balance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Balance',
  },
] as const satisfies IFilter[]

export const PROSPECTS_RECEIVABLES_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'debtor',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Customer',
    title: 'Customer',
  },
  {
    field: '_0_to_30_days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '1-30 Days',
  },
  {
    field: '_31_to_60_days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '31-60 Days',
  },
  {
    field: '_61_to_90_days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '61-90 Days',
  },
  {
    field: '_91_plus_days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '91+ Days',
  },
  {
    field: 'cross_aged',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Cross Aged',
  },
  {
    field: 'concentration_limit',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Conc. Limit',
  },
  {
    field: 'ineligible_ar',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Ineligible AR',
  },
  {
    field: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AR',
  },
  {
    field: 'total_percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    excludeFullSearchFilter: true,
  },
  {
    field: 'eligible_ar',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Eligible AR',
  },
  {
    field: 'available_ar',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Available AR',
  },
  {
    field: 'eligible',
    type: FilterFieldType.List,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: true,
        label: 'Yes',
      },
      {
        value: false,
        label: 'No',
      },
    ],
    isSortable: true,
    title: 'Eligible',
  },
] as const satisfies IFilter[]

export const PROSPECTS_PAYABLES_LIST_FILTERS_CONFIG = [
  {
    field: 'creditor',
    dbField: 'memo',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'mapping',
    type: FilterFieldType.Empty,
    title: 'Vendor',
    align: 'left',
  },
  {
    field: 'prediction',
    type: FilterFieldType.Empty,
    title: 'Prediction',
    align: 'left',
  },
  {
    field: 'balance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Balance',
  },
] as const satisfies IFilter[]

export const PROSPECTS_PAYABLES_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'creditor',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Vendor',
    title: 'Vendor',
  },
  {
    field: '_0_to_30_days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '1-30 Days',
  },
  {
    field: '_31_to_60_days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '31-60 Days',
  },
  {
    field: '_61_to_90_days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '61-90 Days',
  },
  {
    field: '_91_plus_days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '91+ Days',
  },
  {
    field: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AP',
  },
  {
    field: 'total_percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    excludeFullSearchFilter: true,
  },
] as const satisfies IFilter[]

export const PROSPECTS_REPORT_DETAIL_LIST_FILTERS_CONFIG =
  PROSPECTS_PAYABLES_SUMMARY_LIST_FILTERS_CONFIG
